import { getLanguageParam, getOddsParams, getTabValue } from '@sportal365/smp-widget-utils';
import { IOddsBaseConfig, IMatchesV2Response, SDKOptions } from '../../types';
import { generateRequest } from '../http.service';
import { POPULAR } from '../../constants/statusFilters';

export interface ILivescoreMatchesProps extends IOddsBaseConfig {
  statusFilter: string;
  date: string;
  utcOffset: number;
  dataPopularList?: string;
}

export const getLivescoreMatches = async ({
  params,
  sdkOptions,
}: {
  params: ILivescoreMatchesProps;
  sdkOptions: SDKOptions;
}): Promise<IMatchesV2Response> => {
  const url = `${sdkOptions.dataConfigApiUrl}/v2/matches/livescore`;

  const baseParams = {
    date: params.date,
    utc_offset: params.utcOffset,
    status_types: getTabValue(params.statusFilter),
    language_code: getLanguageParam(sdkOptions.dataConfigLang),
    tournament_group: params.statusFilter === POPULAR ? params.dataPopularList : sdkOptions.dataConfigCompetitionList,
  };

  const formatOddsParams = getOddsParams({
    dataConfigOddClient: sdkOptions.dataConfigOddClient,
    ...params,
  });

  const oddsParams = {
    odd_client: formatOddsParams.oddClient ?? undefined,
    odd_format: formatOddsParams.oddFormat ?? undefined,
    market_types: formatOddsParams.marketTypes ?? undefined,
    odd_type: formatOddsParams.oddType ?? undefined,
  };

  const requestParams = {
    ...baseParams,
    ...oddsParams,
  };

  return await generateRequest(url, requestParams, sdkOptions);
};

export interface IMatchesProps extends IOddsBaseConfig {
  team_ids?: string;
  status_types?: string;
  tournament_ids?: string;
  season_ids?: string;
  stage_ids?: string;
  round_filter?: string[];
  round_ids?: string[];
  limit?: string;
  sort_direction?: string;
  startDate?: string;
  endDate?: string;
}

export const getMatches = async ({
  params,
  sdkOptions,
}: {
  params: IMatchesProps;
  sdkOptions: SDKOptions;
}): Promise<IMatchesV2Response> => {
  const url = `${sdkOptions.dataConfigApiUrl}/v2/matches`;

  const baseParams = {
    status_types: params.status_types,
    team_ids: params.team_ids,
    tournament_ids: params.tournament_ids,
    season_ids: params.season_ids,
    stage_ids: params.stage_ids,
    round_filter: params.round_filter,
    round_ids: params.round_ids,
    language_code: getLanguageParam(sdkOptions.dataConfigLang),
    offset: 0,
    limit: params.limit,
    sort_direction: params.sort_direction ?? 'asc',
    from_kickoff_time: params.startDate,
    to_kickoff_time: params.endDate,
  };

  const formatOddsParams = getOddsParams({
    dataConfigOddClient: sdkOptions.dataConfigOddClient,
    ...params,
  });

  const oddsParams = {
    odd_client: formatOddsParams.oddClient ?? undefined,
    odd_format: formatOddsParams.oddFormat ?? undefined,
    market_types: formatOddsParams.marketTypes ?? undefined,
    odd_type: formatOddsParams.oddType ?? undefined,
  };

  const requestParams = {
    ...baseParams,
    ...oddsParams,
  };

  return await generateRequest(url, requestParams, sdkOptions);
};

export interface IMatchByPlayerProps extends IOddsBaseConfig {
  player_id?: string;
  season_ids?: string;
}

export const getMatchByPlayerId = async ({
  params,
  sdkOptions,
}: {
  params: IMatchByPlayerProps;
  sdkOptions: SDKOptions;
}): Promise<IMatchesV2Response> => {
  const url = `${sdkOptions.dataConfigApiUrl}/v2/matches`;

  const baseParams = {
    language_code: getLanguageParam(sdkOptions.dataConfigLang),
    player_id: params.player_id,
    season_ids: params.season_ids,
    status_types: 'NOT_STARTED,LIVE',
    limit: 1,
    offset: 0,
    sort_direction: 'asc',
  };

  const formatOddsParams = getOddsParams({
    dataConfigOddClient: sdkOptions.dataConfigOddClient,
    ...params,
  });

  const oddsParams = {
    odd_client: formatOddsParams.oddClient ?? undefined,
    odd_format: formatOddsParams.oddFormat ?? undefined,
    market_types: formatOddsParams.marketTypes ?? undefined,
    odd_type: formatOddsParams.oddType ?? undefined,
  };

  const requestParams = {
    ...baseParams,
    ...oddsParams,
  };

  return await generateRequest(url, requestParams, sdkOptions);
};

export interface IMatchByTeamProps extends IOddsBaseConfig {
  team_ids?: string;
  season_ids?: string;
}

export const getMatchByTeamId = async ({
  params,
  sdkOptions,
}: {
  params: IMatchByTeamProps;
  sdkOptions: SDKOptions;
}): Promise<IMatchesV2Response> => {
  const url = `${sdkOptions.dataConfigApiUrl}/v2/matches`;

  const baseParams = {
    language_code: getLanguageParam(sdkOptions.dataConfigLang),
    team_ids: params.team_ids,
    season_ids: params.season_ids,
    status_types: 'NOT_STARTED,LIVE',
    limit: 1,
    offset: 0,
    sort_direction: 'asc',
  };

  const formatOddsParams = getOddsParams({
    dataConfigOddClient: sdkOptions.dataConfigOddClient,
    ...params,
  });

  const oddsParams = {
    odd_client: formatOddsParams.oddClient ?? undefined,
    odd_format: formatOddsParams.oddFormat ?? undefined,
    market_types: formatOddsParams.marketTypes ?? undefined,
    odd_type: formatOddsParams.oddType ?? undefined,
  };

  const requestParams = {
    ...baseParams,
    ...oddsParams,
  };

  return await generateRequest(url, requestParams, sdkOptions);
};

export interface IMatchByIdProps extends IOddsBaseConfig {
  match_ids?: string;
}

export const getMatchById = async ({
  params,
  sdkOptions,
}: {
  params: IMatchByIdProps;
  sdkOptions: SDKOptions;
}): Promise<IMatchesV2Response> => {
  const url = `${sdkOptions.dataConfigApiUrl}/v2/matches/livescore`;

  const baseParams = {
    language_code: getLanguageParam(sdkOptions.dataConfigLang),
    match_ids: params.match_ids,
    limit: 1,
    offset: 0,
  };

  const formatOddsParams = getOddsParams({
    dataConfigOddClient: sdkOptions.dataConfigOddClient,
    ...params,
  });

  const oddsParams = {
    odd_client: formatOddsParams.oddClient ?? undefined,
    odd_format: formatOddsParams.oddFormat ?? undefined,
    market_types: formatOddsParams.marketTypes ?? undefined,
    odd_type: formatOddsParams.oddType ?? undefined,
  };

  const requestParams = {
    ...baseParams,
    ...oddsParams,
  };

  return await generateRequest(url, requestParams, sdkOptions);
};
